import * as React from 'react';
import {Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter, Button, OverlayTrigger, Popover} from 'react-bootstrap';
import datafangstHelpers from '../../../../utilities';
import datacatalog, {AttributeType, SensitiveType} from '../../../../integration/datacatalog/datacatalog-services';
import FeaturesState from '../domain/FeaturesState';
import * as selectors from '../selectors';
import {connect} from 'react-redux';
import {Spinner} from '../../../shared-components/status/Spinner';
import {Operation} from '../domain';

interface VegkartImportModalProps {
    checkIfSensitiveAttributesOnFeatureForCurrentUser: (url: string) => AttributeType[];
    checkIfSensitiveAttributesOnFeatureForAllUsers: (url: string, operation: Operation, callback?: (sensitive: boolean) => void) => boolean;
    emptySensitiveAttributeTypes: () => void;
    importVegkartObjects: (url: string, operation: string) => void;
    canImport: boolean;
    userSensitiveTypes: string[];
    datacatalog: datacatalog;
    missingAttributeTypes: AttributeType[];
    fetchingMissingAttributeTypes: boolean;
    fetchingSensitiveDataInURL: boolean;
    sensitiveDataInUrl: boolean;
}

const mapStateToProps = (state: FeaturesState) => {
    return {
        missingAttributeTypes: selectors.missingAttributeTypes(state),
        fetchingMissingAttributeTypes: selectors.fetchingMissingAttributeTypes(state),
        fetchingSensitiveDataInURL: selectors.fetchingSensitiveDataOnUrl(state),
        sensitiveDataInUrl: selectors.sensitiveDataOnUrl(state)
    };
};

const mapDispatchToProps = ({
});

interface VegkartImportModalState {
    operation: string;
    showModal: boolean;
    showSensitiveModal: boolean;
    apiUrl: string;
    validUrl: boolean;
    v2Url: boolean;
    hasAllPermissionsForFeatureType: boolean;
}

class VegkartImportModal extends React.Component<VegkartImportModalProps, VegkartImportModalState> {

    constructor(props: VegkartImportModalProps) {
        super(props);
        this.state = {
            showModal: false,
            showSensitiveModal: false,
            operation: 'Oppdater',
            apiUrl: '',
            validUrl: false,
            v2Url: false,
            hasAllPermissionsForFeatureType: true
        };

        // Bindings to handle this in event handlers
        this.setDefaultState = this.setDefaultState.bind(this);
        this.submitURL = this.submitURL.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.getFeatureTypeIdFromURL = this.getFeatureTypeIdFromURL.bind(this);
        this.userHasPermissionsForAttributesInFeatureType = this.userHasPermissionsForAttributesInFeatureType.bind(this);
        this.anyoneHasPermissionsForAttributesInFeatureType = this.anyoneHasPermissionsForAttributesInFeatureType.bind(this);
        this.checkTypePermissionsFromURL = this.checkTypePermissionsFromURL.bind(this);
        this.getPermissionsNeededForFeatureType = this.getPermissionsNeededForFeatureType.bind(this);
    }

    setDefaultState() {
        this.props.emptySensitiveAttributeTypes();
        this.setState({showModal: false, showSensitiveModal: false, operation: 'Oppdater', apiUrl: '', validUrl: false});
    }

    submitURL() {
        const operation = datafangstHelpers.operationToDomain(this.state.operation);
        this.props.importVegkartObjects(this.state.apiUrl, operation);
        this.setDefaultState();
    }

    handleSubmit() {
        if (this.props.missingAttributeTypes.length > 0) {
            this.submitURL();
        } else {
            const operation = datafangstHelpers.operationToDomain(this.state.operation);
            if (this.anyoneHasPermissionsForAttributesInFeatureType(this.getFeatureTypeIdFromURL(this.state.apiUrl))) {
                this.submitURL();
            } else {
                this.props.checkIfSensitiveAttributesOnFeatureForAllUsers(this.state.apiUrl, operation, (sensitive => {
                    if (sensitive) {
                        this.setState({showSensitiveModal: true});
                    } else {
                        this.submitURL();
                    }
                }));
            }
        }
    }

    updateText(text: string) {
        const url = text.trim(); // Trim leading/trailing whitespaces from user input url
        const validUrl = this.isValidUrl(text);
        const v2Url = url.length > 0 ? /.*(nvdbapiles-v2|\/nvdb\/api\/v2).*/.test(url) : false;
        this.setState({apiUrl: url, validUrl, v2Url});
    }

    isValidUrl(url: string): boolean {
        const urlArray = url.split('?');
        if (urlArray.length !== 2) {
            return false;
        }
        const mainUrl = urlArray[0].split('/');
        if (mainUrl.length !== 5) {
            return false;
        }
        if (mainUrl[2] !== 'nvdbapiles-v3.atlas.vegvesen.no' && mainUrl[2] !== 'nvdbapiles.atlas.vegvesen.no') {
            return false;
        }
        if (mainUrl[3] !== 'vegobjekter') {
            return false;
        }
        return true;
    }

    getFeatureTypeIdFromURL(url: string) {
        const matches: RegExpMatchArray = url.match('vegobjekter/[0-9]+');
        if (matches && matches[0]) {
            const parsed = Number.parseInt(matches[0].split('/')[1], 10);
            if (!Number.isNaN(parsed)) {
                return parsed;
            }
        }
        throw new Error('Kan ikke lese typeid fra url');
    }

    getPermissionsNeededForFeatureType(typeId: number): Omit<SensitiveType, '0'>[] {
        return Object.values(this.props.datacatalog.getFeatureType(typeId).attributeTypes)
            .map(attributeType => attributeType.sensitiveType)
            .filter(sensitive => sensitive !== '0')
            .reduce((prev, curr) => prev.indexOf(curr) < 0 ? prev.concat(curr) : prev, [] as Omit<SensitiveType, '0'>[]);
    }

    userHasPermissionsForAttributesInFeatureType(typeId: number) {
        const permissions = this.getPermissionsNeededForFeatureType(typeId);
        return permissions.length === 0 || permissions.every(permission => this.props.userSensitiveTypes.includes(permission as string));
    }

    anyoneHasPermissionsForAttributesInFeatureType(typeId: number) {
        return this.getPermissionsNeededForFeatureType(typeId).length === 0;
    }

    checkTypePermissionsFromURL(text: string): boolean {
        const typeId = this.getFeatureTypeIdFromURL(text);
        const hasPermissions = this.userHasPermissionsForAttributesInFeatureType(typeId);
        this.setState({hasAllPermissionsForFeatureType: hasPermissions});
        return hasPermissions;
    }

    urlChanged(text: string) {
        this.updateText(text);
        const hasAllPermissionsForFeatureType = this.checkTypePermissionsFromURL(text);
        if (this.isValidUrl(text) && !hasAllPermissionsForFeatureType) {
            this.props.checkIfSensitiveAttributesOnFeatureForCurrentUser(text);
        }
    }

    popoverHoverFocus(explanation: string) {
        return (
        <Popover id="popover-trigger-hover-focus">
            {explanation}
        </Popover>);
   }

    render()  {

        const operations = {'Korriger': 'Endrer egenskapene til en eksisterende vegobjektversjon. Brukes typisk når det er feilregistrerte egenskapsverdier.',
                            'Oppdater': 'Lager ny versjon av eksisterende vegobjekt. Brukes typisk når det har skjedd observerbare endringer på et vegobjekt langs vegen.',
                            'Lukk': 'Setter sluttdato på et eksisterende vegobjekt. Brukes typisk når et vegobjekt er fjernet fra vegen.'};

        const operationsSelector =
            <div>
                <h3>Velg operasjon</h3>
                <ul className={'list-unstyled'}>
                    {Object.entries(operations).map(([operation, operationExplanation]) =>
                        <li key={operation}>
                            <label>
                                <input type="radio"
                                       checked={(this.state as any).operation == operation} value={operation}
                                       onChange={(e) => this.setState({showModal: true, operation: e.target.value})}/>
                                {' ' + operation}
                            </label>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={this.popoverHoverFocus(operationExplanation)}>
                                <i
                                    className="fas fa-question-circle"
                                    style={{padding: '5px', color: 'lightgrey'}}
                                />
                            </OverlayTrigger>
                        </li>
                    )}
                </ul>
            </div>;


        return (
            <div>
                <Button disabled={!this.props.canImport} onClick={() => this.setState({showModal: true})}>
                    Importer
                </Button>
                {this.state.showModal &&
                <Modal show={this.state.showModal} onHide={this.setDefaultState}>
                    <ModalHeader>
                        <ModalTitle>Import av vegobjekter fra Vegkart</ModalTitle>
                    </ModalHeader>
                    <ModalBody>
                        <p>Bruk Vegkarts "Eksporter til API" og lim inn lenken.</p>

                        {this.state.v2Url &&
                        <p className={'infobox'}>API versjon 2 kan ikke lenger benyttes for import fra NVDB.
                            Benytt API versjon 3 for å importere.
                            Ved bruk av Vegkart for å generere lenke, må den nå hentes
                            fra <a href="https://vegkart.atlas.vegvesen.no">"nye" Vegkart</a>,
                            Vegkart 2019 kan ikke lenger benyttes for dette.</p>
                        }

                        <textarea className="vegkart-import-modal-textarea"
                                  onChange={(text) => this.urlChanged(text.target.value)}
                                  value={this.state.apiUrl}
                                  disabled={this.props.fetchingMissingAttributeTypes}
                        />

                        {this.state.validUrl && !this.state.hasAllPermissionsForFeatureType && this.props.fetchingMissingAttributeTypes &&
                            <div style={{'display': 'flex'}}>
                                <span className="inline-spinner-orange-small" style={{'display': 'flex'}}/>
                                <p style={{'display': 'flex', 'alignSelf': 'center', 'margin': '0 0 0 10px'}}>Sjekker om du mangler sensitive rettigheter.</p>
                                <br/>
                            </div>
                        }

                        {this.state.validUrl && !this.props.fetchingMissingAttributeTypes && this.props.missingAttributeTypes.length > 0 &&
                            <div className={'infobox'}>
                                Vegobjektene du er i ferd med å importere inneholder sensitive egenskaper du ikke har
                                rettigheter til:
                                <ul style={{'margin': '10px 0'}}>
                                    {this.props.missingAttributeTypes.map(attributeType => {
                                        return (
                                            <li key={attributeType.id}>{attributeType.name}</li>
                                        );
                                    })}
                                </ul>

                                Du kan importere vegobjektene, men disse egenskapene vil bli utelatt. Dersom du sender
                                inn endringer på objektene vil de sensitive dataene likevel ivaretas.
                            </div>
                        }

                        {this.state.validUrl && !this.state.hasAllPermissionsForFeatureType && !this.props.fetchingMissingAttributeTypes && this.props.missingAttributeTypes.length === 0 &&
                            <div style={{'height': '1.5em', 'display': 'flex'}}>
                                <div style={{'display': 'flex', 'height': '1.5em', 'width': '1.5em'}}>
                                    <i className={'fas fa-check'} style={{'display': 'flex', 'alignSelf': 'center', 'color': 'green', 'fontSize': '1.5em'}}></i>
                                </div>
                                <p style={{'margin': '0 0 0 10px', 'display': 'flex', 'alignSelf': 'center'}}>Du har mulighet til å laste inn alle eksisterende attributter på disse objektene.</p>
                            </div>
                        }

                        {operationsSelector}
                    </ModalBody>
                    <ModalFooter>
                        <Button bsStyle="default" onClick={this.setDefaultState}>Avbryt</Button>
                        <Button bsStyle="primary" disabled={!this.state.operation || !this.state.validUrl || this.props.fetchingMissingAttributeTypes}
                                onClick={this.handleSubmit}>OK</Button>
                    </ModalFooter>
                </Modal>
                }
                <Modal show={this.props.fetchingSensitiveDataInURL || this.state.showSensitiveModal} onHide={this.setDefaultState}>
                    {
                        this.state.showSensitiveModal ?
                            <>
                                <ModalHeader>Advarsel</ModalHeader>
                                <ModalBody>Du importerer nå data med sensitivitet. Disse vil bli synlig for alle med tilgang til kontrakten. Det er ditt ansvar å påse at integriteten ivaretas. Vil du fortsette importen til kontrakten?</ModalBody>
                                <ModalFooter>
                                    <Button onClick={this.setDefaultState}>Nei</Button>
                                    <Button onClick={this.submitURL}>Ja</Button>
                                </ModalFooter>
                            </> :
                            <Spinner/>

                    }
                </Modal>
            </div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(VegkartImportModal);